import { Subscription } from 'rxjs';
import { ReportElement, Location, ReportPaperSize, ReportPageMargin, ElementType } from '../../../_objects/report-element';
import { ReportPreviewMetadata, PropertyPreviewMetadata, FieldType, UpdateMetadata } from '../../../_services/offline-report-preview.service';
import { ReportCommonSection } from './report-common-section';
import { TemplatePageSection } from './report-template';
import { ImageComponent, TableData } from './TableData';

export class ReportPage extends ReportElement {
  public isDrawMode: boolean;
  public hasHeader: boolean;
  public hasFooter: boolean;
  public paperWidth: string;
  public paperHeight: string;
  public bodyHeight: string;
  public headerHeight: string;
  public footerHeight: string;
  public paddingTop: string;
  public paddingBottom: string;
  public paddingLeft: string;
  public paddingRight: string;
  public paperSize: ReportPaperSize;
  public summaryDataList: TableData[];
  public pictureDataList: TableData[];
  public dataPlateDataList: ImageComponent[];
  public summaryFirst = false;
  public pictureFirst = false;
  public dataPlateFirst = false;
  public backToSummaryId: string;

  private header: ReportCommonSection;
  private footer: ReportCommonSection;
  private pageMargin: ReportPageMargin;

  private baseDef: TemplatePageSection;
  private readonly metaId = 'id';
  private readonly metaHeight = 'height';
  private readonly metaHasHeader = 'hasHeader';
  private readonly metaHasFooter = 'hasFooter';

  constructor(
    index: number,
    header: ReportCommonSection, footer: ReportCommonSection,
    paperSize: ReportPaperSize, pageMargin: ReportPageMargin,
    templateBody?: TemplatePageSection
  ) {

    super(ElementType.Page, index, !(templateBody == null));

    this.header = header;
    this.footer = footer;
    this.paperSize = paperSize;
    this.pageMargin = pageMargin;

    if (templateBody) {
      this.baseDef = templateBody;
    } else {
      this.baseDef = {
        id: this.generateUUID(),
        height: 0,
        hasHeader: true,
        hasFooter: true,
        texts: [],
        images: [],
        lines: [],
        rectangles: [],
        inspectionSummary: null,
        inspectionPictures: null,
        dataPlates: null,
        measurementPoints: [],
        signatures: []
      };
    }

    this.isDrawMode = false;
    this.hasHeader = this.baseDef.hasHeader;
    this.hasFooter = this.baseDef.hasFooter;

    let marginWidth = pageMargin.getLeftNum()?pageMargin.getLeftNum():0;
    marginWidth += pageMargin.getRightNum()?pageMargin.getRightNum():0;
    this.paperWidth = [paperSize.getWidthNum()-marginWidth,'mm'].join('');
    let marginHeight = pageMargin.getTopNum()?pageMargin.getTopNum():0;
    marginHeight += pageMargin.getBottomNum()?pageMargin.getBottomNum():0;
    this.paperHeight = [paperSize.getHeightNum()-marginHeight,'mm'].join('');

    this.paperHeight = paperSize.getHeight();
    this.paddingTop = pageMargin.getTop();
    this.paddingBottom = pageMargin.getBottom();
    this.paddingLeft = pageMargin.getLeft();
    this.paddingRight = pageMargin.getRight();
    this.headerHeight = header.getHeight();
    this.footerHeight = footer.getHeight();
    this.bodyHeight = this.getBodyHeight();

    this.setChainUpdate();
    this.setPreviewMetadata();
  }

  public setDrawData(metadata: ReportPreviewMetadata) {
    // NOP
  }
  public setDropData(metadata: ReportPreviewMetadata) {
    // NOP
  }
  public setResizeData(metadata: ReportPreviewMetadata) {
    // NOP
  }

  public getAnchorId(): string {
    return 'p' + (this.getIndex() + 1);
  }

  public getTopPx(): number {
    return 0; // NOP
  }

  public getLeftPx(): number {
    return 0; // NOP
  }

  public getHeight(location: Location): string {
    let rtn: string;
    switch (location) {
      case Location.Header:
        rtn = this.header.getHeight();
        break;
      case Location.Body:
        rtn = this.getBodyHeight();
        break;
      case Location.Footer:
        rtn = this.footer.getHeight();
        break;
      case Location.Page:
        rtn = this.paperSize.getHeight();
        break;
    }
    return rtn;
  }

  public getHeightNum(location: Location): number {
    let rtn: number;
    switch (location) {
      case Location.Header:
        rtn = this.header.getHeightNum();
        break;
      case Location.Body:
        rtn = this.getBodyHeightNum();
        break;
      case Location.Footer:
        rtn = this.footer.getHeightNum();
        break;
      case Location.Page:
        rtn = this.paperSize.getHeightNum();
        break;
    }
    return rtn;
  }

  public getHeightPx(location?: Location): number {

    if (!location) { return 0; }

    let rtn: number;

    switch (location) {
      case Location.Header:
        rtn = this.header.getHeightPx();
        break;
      case Location.Body:
        rtn = this.getBodyHeightPx();
        break;
      case Location.Footer:
        rtn = this.footer.getHeightPx();
        break;
      case Location.Page:
        rtn = this.paperSize.getHeightPx();
        break;
    }
    return rtn;
  }

  public getWidth(location: Location): string {
    let rtn: string;
    switch (location) {
      case Location.Header:
      case Location.Body:
      case Location.Footer:
        rtn = this.getCoreWidth();
        break;
      case Location.Page:
        rtn = this.paperSize.getWidth();
        break;
    }
    return rtn;
  }

  public getWidthNum(location: Location): number {
    let rtn: number;
    switch (location) {
      case Location.Header:
      case Location.Body:
      case Location.Footer:
        rtn = this.getCoreWidthNum();
        break;
      case Location.Page:
        rtn = this.paperSize.getWidthNum();
        break;
    }
    return rtn;
  }

  public getWidthPx(location?: Location): number {
    if (!location) { return 0; }

    let rtn: number;
    switch (location) {
      case Location.Header:
      case Location.Body:
      case Location.Footer:
        rtn = this.getCoreWidthPx();
        break;
      case Location.Page:
        rtn = this.paperSize.getWidthPx();
        break;
    }
    return rtn;
  }

  public getTemplateJSON(): TemplatePageSection {
    return this.baseDef;
  }

  protected getPreviewMetadata(): PropertyPreviewMetadata[] {
    return [
      {
        name: this.metaHasHeader, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'CheckBox',
        selectItems: [],
        isRequired: true,
        isEdited: false,
      },
      {
        name: this.metaHasFooter, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'CheckBox',
        selectItems: [],
        isRequired: true,
        isEdited: false,
      },
    ];
  }

  protected setUpdateMetadata(metadata: UpdateMetadata) {
    switch (metadata.name) {
      case this.metaId:
        this.baseDef[metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaHeight:
        this.baseDef[metadata.name] = metadata.value as number;
        this.setPreviewValue(metadata.name, metadata.value);
        this.bodyHeight = this.getBodyHeight();
        break;
      case this.metaHasHeader:
        this.baseDef[metadata.name] = metadata.value as boolean;
        this.setPreviewValue(metadata.name, metadata.value);
        this.hasHeader = metadata.value as boolean;
        break;
      case this.metaHasFooter:
        this.baseDef[metadata.name] = metadata.value as boolean;
        this.setPreviewValue(metadata.name, metadata.value);
        this.hasFooter = metadata.value as boolean;
        break;
    }
  }

  protected setChainUpdateFunction(subscribers: Array<Subscription>) {
    subscribers.push(
      this.paperSize.onUpdatePaperSize.subscribe((metadata: UpdateMetadata) => {
        this.paperWidth = this.paperSize.getWidth();
        this.paperHeight = this.paperSize.getHeight();
      })
    );
    subscribers.push(
      this.pageMargin.onUpdateTop.subscribe((metadata: UpdateMetadata) => {
        this.paddingTop = this.pageMargin.getTop();
        this.bodyHeight = this.getBodyHeight();
      })
    );
    subscribers.push(
      this.pageMargin.onUpdateBottom.subscribe((metadata: UpdateMetadata) => {
        this.paddingBottom = this.pageMargin.getBottom();
        this.bodyHeight = this.getBodyHeight();
      })
    );
    subscribers.push(
      this.pageMargin.onUpdateLeft.subscribe((metadata: UpdateMetadata) => {
        this.paddingLeft = this.pageMargin.getLeft();
      })
    );
    subscribers.push(
      this.pageMargin.onUpdateRight.subscribe((metadata: UpdateMetadata) => {
        this.paddingRight = this.pageMargin.getRight();
      })
    );
    subscribers.push(
      this.header.onUpdateHeight.subscribe((metadata: UpdateMetadata) => {
        this.headerHeight = this.header.getHeight();
      })
    );
    subscribers.push(
      this.footer.onUpdateHeight.subscribe((metadata: UpdateMetadata) => {
        this.footerHeight = this.footer.getHeight();
      })
    );
  }

  private getBodyHeightNum(): number {
    let rtn = this.paperSize.getHeightNum();
    rtn = this.pageMargin.getPaddedHeightNum(rtn);
    if (this.baseDef.hasHeader) {
      rtn = rtn - this.header.getHeightNum();
    }
    if (this.baseDef.hasFooter) {
      rtn = rtn - this.footer.getHeightNum();
    }
    rtn = this.round(rtn);
    return rtn;
  }
  private getBodyHeight(): string {
    return [this.getBodyHeightNum(), 'mm'].join('');
  }
  private getBodyHeightPx(): number {
    return this.toPixel(this.getBodyHeightNum(), 'mm');
  }

  private getCoreWidthNum(): number {
    let rtn = this.paperSize.getWidthNum();
    rtn = this.pageMargin.getPaddedWidthNum(rtn);
    rtn = this.round(rtn);
    return rtn;
  }

  private getCoreWidth(): string {
    return [this.getCoreWidthNum(), 'mm'].join('');
  }

  private getCoreWidthPx(): number {
    return this.toPixel(this.getCoreWidthNum(), 'mm');
  }

  getMetadataId() {
    return this.baseDef[this.metaId];
  }

  hasInspectionSummary(): boolean {
    return this.baseDef.inspectionSummary !== null;
  }

  hasInspectionPictures(): boolean {
    return this.baseDef.inspectionPictures !== null;
  }

  hasInspectionDataPlates(): boolean {
    return this.baseDef.dataPlates !== null;
  }

  getInspectionSummary() {
    return this.baseDef.inspectionSummary;
  }
}
